import React, { useState } from 'react';
import { useVeo } from '../hooks/useVeo';
import { Spinner } from './Spinner';

export const VideoGenerator = ({ uploadedFile }) => {
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [prompt, setPrompt] = useState('A dynamic 8-second product showcase video of this item. Use a slow 360-degree spin or a smooth panning shot with a clean, professional background.');
  const { 
    generateVideo, 
    isGenerating, 
    videoUrl, 
    statusMessage, 
    error 
  } = useVeo();

  const handleGenerateClick = () => {
    if (!prompt.trim()) {
      alert("Please enter a prompt for the video.");
      return;
    }
    generateVideo(uploadedFile, aspectRatio, prompt);
  };

  return (
    <div className="bg-[#270b79] bg-opacity-50 p-6 rounded-2xl shadow-2xl border border-[#F06B32] border-opacity-30">
      <div className="flex flex-col gap-6 mb-8">
        <div>
          <label htmlFor="video-prompt" className="block font-semibold text-white mb-3 text-lg">Video Generation Prompt:</label>
          <textarea
            id="video-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            className="w-full bg-[#1a074f] border-2 border-[#270b79] text-white text-sm rounded-xl focus:ring-2 focus:ring-[#F06B32] focus:border-[#F06B32] block p-4 min-h-[100px]"
            disabled={isGenerating}
          />
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-4 bg-[#1a074f] p-4 rounded-xl">
          <label className="font-semibold text-white text-sm">Video Format:</label>
          <select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            className="bg-[#270b79] border-2 border-[#270b79] text-white text-sm rounded-lg p-3"
            disabled={isGenerating}
          >
            <option value="16:9">16:9 (Landscape)</option>
            <option value="9:16">9:16 (Portrait)</option>
          </select>
          <button
            onClick={handleGenerateClick}
            disabled={isGenerating}
            className="w-full sm:w-auto bg-[#F06B32] hover:bg-[#e55a27] disabled:bg-gray-600 text-white font-bold py-3 px-8 rounded-xl flex items-center justify-center"
          >
            {isGenerating && <Spinner className="w-5 h-5 mr-2" />}
            {isGenerating ? 'Creating Video...' : 'Generate Video'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-900 bg-opacity-50 border border-red-700 rounded-xl">
          <p className="text-red-300 text-center">{error}</p>
        </div>
      )}
      
      <div className="aspect-video bg-[#1a074f] rounded-xl flex items-center justify-center relative overflow-hidden border-2 border-[#270b79]">
        {isGenerating && (
          <div className="text-center p-8">
            <Spinner className="w-16 h-16 text-[#F06B32] mx-auto mb-4" />
            <p className="text-white font-semibold text-lg mb-2">{statusMessage}</p>
            <p className="text-gray-400 text-sm">This may take a few minutes...</p>
          </div>
        )}
        
        {/* ✅ Direct video URL with API key works perfectly in video tag */}
        {videoUrl && !isGenerating && (
          <>
            <video 
              src={videoUrl} 
              controls 
              autoPlay 
              loop 
              className="w-full h-full object-contain rounded-lg"
            />
            <a
              href={videoUrl}
              download="ai-generated-video.mp4"
              className="absolute bottom-6 right-6 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-xl"
            >
              ⬇️ Download Video
            </a>
          </>
        )}
        
        {!isGenerating && !videoUrl && (
          <div className="text-center text-gray-400 p-8">
            <div className="w-20 h-20 bg-[#270b79] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-[#F06B32]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Ready to Create Amazing Videos</h3>
            <p className="text-gray-300">Your AI-generated product video will appear here.</p>
          </div>
        )}
      </div>
    </div>
  );
};